import type { PaymentIntentResponse } from "@/api/online-ordering-service/model";

declare global {
  interface Window {
    FawryPay?: { checkout: (req: unknown, cfg: unknown) => void };
    DISPLAY_MODE?: { POPUP: string; SEPARATED: string; INSIDE_PAGE: string };
  }
}

let scriptLoadPromise: Promise<void> | null = null;
let cssLoadPromise: Promise<void> | null = null;

function fawryAssetBase(testMode: boolean): string {
  return testMode
    ? "https://atfawry.fawrystaging.com/atfawry/plugin/assets/payments"
    : "https://www.atfawry.com/atfawry/plugin/assets/payments";
}

// Fawry's plugin script does NOT inject its own CSS — it only outputs
// DOM with classes (modal-f, modal_content, responsive-iframe). Without
// the matching stylesheet the iframe renders as an unstyled 300x150 box
// at the bottom of the document, which looks like "all calls 200 but
// nothing on UI". Load the CSS in parallel with the JS.
function loadFawryStylesheet(testMode: boolean): Promise<void> {
  if (cssLoadPromise) return cssLoadPromise;
  cssLoadPromise = new Promise<void>((resolve) => {
    const existing = document.querySelector('link[href*="fawrypay-payments.css"]');
    if (existing) {
      resolve();
      return;
    }
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = `${fawryAssetBase(testMode)}/css/fawrypay-payments.css`;
    // Don't reject on stylesheet failure — JS still runs and we'd rather
    // show an unstyled frame than block the whole flow.
    link.onload = () => resolve();
    link.onerror = () => resolve();
    document.head.appendChild(link);
  });
  return cssLoadPromise;
}

function loadFawryScript(testMode: boolean): Promise<void> {
  if (window.FawryPay) return Promise.resolve();
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector('script[src*="fawrypay-payments"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("FawryPay script failed")), { once: true });
      return;
    }
    const src = `${fawryAssetBase(testMode)}/js/fawrypay-payments.js`;
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptLoadPromise = null;
      reject(new Error("FawryPay script failed to load"));
    };
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

function resolveSdk(): { checkout: (req: unknown, cfg: unknown) => void } | null {
  if (window.FawryPay) return window.FawryPay;
  try {
    // eslint-disable-next-line @typescript-eslint/no-implied-eval, no-new-func
    const fn = new Function('return typeof FawryPay !== "undefined" ? FawryPay : null');
    return fn() as { checkout: (req: unknown, cfg: unknown) => void } | null;
  } catch {
    return null;
  }
}

export type FawryCallbacks = {
  onSuccess?: (response: unknown) => void;
  onFailure?: (error: unknown) => void;
  /**
   * Localized message shown on the loader overlay while Fawry's iframe
   * boots (the iframe makes 3 cascading API calls before painting, so
   * there's a multi-second blank gap by default).
   */
  loadingMessage?: string;
};

const FAWRY_LOADER_ID = "fawry-loader";

function showFawryLoader(message: string): void {
  if (document.getElementById(FAWRY_LOADER_ID)) return;
  const overlay = document.createElement("div");
  overlay.id = FAWRY_LOADER_ID;
  overlay.setAttribute("role", "status");
  overlay.setAttribute("aria-live", "polite");
  const card = document.createElement("div");
  card.className = "fawry-loader-card";
  const spinner = document.createElement("div");
  spinner.className = "fawry-loader-spinner";
  const text = document.createElement("div");
  text.className = "fawry-loader-text";
  text.textContent = message;
  card.appendChild(spinner);
  card.appendChild(text);
  overlay.appendChild(card);
  document.body.appendChild(overlay);
}

function hideFawryLoader(): void {
  const node = document.getElementById(FAWRY_LOADER_ID);
  if (node) node.remove();
}

/**
 * Wait for Fawry's iframe to be injected by ``sdk.checkout`` and finish
 * loading its initial document, then fire ``onReady`` after a short
 * grace period (the iframe still needs to make its API calls before
 * the form paints, but we can't observe those from across origins).
 *
 * Falls back to firing after a hard cap so a stuck iframe never leaves
 * the loader on screen forever.
 */
function watchFawryIframeReady(onReady: () => void): void {
  const startedAt = Date.now();
  const POLL_MS = 100;
  const HARD_CAP_MS = 12_000;
  const POST_LOAD_GRACE_MS = 700;

  const interval = window.setInterval(() => {
    const iframe = document.querySelector(
      "#fawry-payments iframe",
    ) as HTMLIFrameElement | null;

    if (iframe) {
      window.clearInterval(interval);
      const fire = () => window.setTimeout(onReady, POST_LOAD_GRACE_MS);
      // Cross-origin iframes don't expose contentDocument, so the
      // `load` event is our only signal that the URL finished loading.
      iframe.addEventListener("load", fire, { once: true });
      // Hard cap regardless of load event — slow networks shouldn't
      // strand the user behind our loader.
      window.setTimeout(onReady, HARD_CAP_MS - (Date.now() - startedAt));
      return;
    }

    if (Date.now() - startedAt > HARD_CAP_MS) {
      window.clearInterval(interval);
      onReady();
    }
  }, POLL_MS);
}

export async function launchFawryCheckout(
  intent: PaymentIntentResponse,
  callbacks: FawryCallbacks,
): Promise<void> {
  const metadata = (intent.metadata ?? {}) as Record<string, unknown>;
  const testMode = Boolean(metadata.testMode);

  // Show the loader BEFORE awaiting any network — script + stylesheet
  // loads themselves can take a second or two on cold connections, and
  // sdk.checkout() injects the iframe immediately but the form inside
  // takes another few seconds to paint. The loader bridges both gaps.
  showFawryLoader(
    callbacks.loadingMessage ?? "Connecting to Fawry, please wait…",
  );

  try {
    await Promise.all([loadFawryScript(testMode), loadFawryStylesheet(testMode)]);
  } catch (err) {
    hideFawryLoader();
    throw err;
  }

  const sdk = resolveSdk();
  if (!sdk) {
    hideFawryLoader();
    throw new Error("FawryPay SDK not available after load");
  }

  // Fawry merchants typically have EDIT_MOBILE_EMAIL=false /
  // SKIPP_MOBILE_EMAIL=false, in which case the plugin renders email and
  // mobile as readonly and refuses to proceed when they're empty. The
  // backend now passes the customer's contact info on the intent metadata;
  // forward it here so the Pay button isn't deadlocked.
  // ``saveCardInfo`` opts the user into Fawry's tokenization-during-
  // checkout flow: Fawry binds the new card to the customerProfileId,
  // and we retrieve it server-side after the PAID webhook (Fawry never
  // returns the token inline). The signature does NOT include this
  // field, so we don't need to recompute it on the frontend.
  const chargeRequest = {
    merchantCode: metadata.merchantCode,
    merchantRefNum: metadata.merchantRefNum,
    customerProfileId: metadata.customerProfileId ?? "",
    customerEmail: (metadata.customerEmail as string) ?? "",
    customerMobile: (metadata.customerMobile as string) ?? "",
    paymentExpiry: "",
    chargeItems: metadata.chargeItems ?? [],
    returnUrl: metadata.returnUrl ?? "",
    authCaptureModePayment: false,
    saveCardInfo: Boolean(metadata.saveCardInfo),
    signature: metadata.signature,
  };

  const config = {
    locale: "en",
    mode: window.DISPLAY_MODE?.POPUP ?? "POPUP",
    returnUrl: metadata.returnUrl ?? "",
    onSuccess: (data: unknown) => callbacks.onSuccess?.(data),
    onFailure: (error: unknown) => callbacks.onFailure?.(error),
  };

  sdk.checkout(chargeRequest, config);
  watchFawryIframeReady(hideFawryLoader);
}

export function cleanupFawry(): void {
  try {
    hideFawryLoader();
    const div = document.getElementById("fawry-payments");
    if (div) div.remove();
  } catch (err) {
    console.warn("[fawry] cleanup error", err);
  }
}
